'use client'

import { useEffect, useRef, type RefObject } from 'react'

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      el.getAttribute('aria-hidden') !== 'true' &&
      el.tabIndex !== -1 &&
      el.offsetParent !== null,
  )
}

export type UseFocusTrapOptions = {
  active: boolean
  onEscape?: () => void
  initialFocusRef?: RefObject<HTMLElement | null>
  restoreFocus?: boolean
}

/**
 * Traps Tab focus inside `containerRef` while `active`, handles Escape,
 * and optionally restores focus on deactivate.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  {
    active,
    onEscape,
    initialFocusRef,
    restoreFocus = true,
  }: UseFocusTrapOptions,
): void {
  const previouslyFocused = useRef<HTMLElement | null>(null)
  const onEscapeRef = useRef(onEscape)
  onEscapeRef.current = onEscape

  useEffect(() => {
    if (!active) return

    const container = containerRef.current
    if (!container) return

    previouslyFocused.current =
      (document.activeElement as HTMLElement | null) ?? null

    const focusInitial = () => {
      const target =
        initialFocusRef?.current ?? getFocusable(container)[0] ?? container
      target.focus({ preventScroll: true })
    }

    const frame = requestAnimationFrame(() => {
      focusInitial()
    })

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation()
        onEscapeRef.current?.()
        return
      }

      if (e.key !== 'Tab') return

      const focusable = getFocusable(container)
      if (focusable.length === 0) {
        e.preventDefault()
        container.focus()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const current = document.activeElement as HTMLElement | null

      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault()
          last.focus()
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('keydown', onKeyDown, true)
      if (restoreFocus) {
        previouslyFocused.current?.focus?.()
      }
    }
  }, [active, containerRef, initialFocusRef, restoreFocus])
}
